// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

type customCheckoutState = Readonly<{
    checkoutStep: number;
    notes: string;
    carrier: string;
    shipVia: string;
    nameOfCarrier: string;
    accountNumber: string;
    shippingNotes: string;
    lastFour: string;
    expDateMon: string;
    expDateYear: string;
    ccType: string;
    po: string;
    paymentMethod: string;
    paymentProfile: string;
    contractNumber: string;
    isEditingAddress: boolean;
    showErrors: boolean;
    isPaymentReady: boolean;
    useBillingAddress: boolean;
    updateAssociatedSalesRep: boolean;
    associatedSalesRep: string;
}>;

const initialCustomCheckoutState: customCheckoutState = {
    checkoutStep: 0,
    notes: "",
    carrier: "",
    shipVia: "",
    nameOfCarrier: "",
    accountNumber: "",
    shippingNotes: "",
    lastFour: "",
    expDateMon: "",
    expDateYear: "",
    ccType: "",
    po: "",
    paymentMethod: "",
    paymentProfile: "",
    contractNumber: "",
    isEditingAddress: false,
    showErrors: false,
    isPaymentReady: false,
    useBillingAddress: true,
    updateAssociatedSalesRep: false,
    associatedSalesRep: "",
};

const reducer = {
    "xCheckoutStepper/update": (draft: Draft<customCheckoutState>, action: { step: number }) => {
        draft.checkoutStep = action.step;
    },
    "xCheckoutNotes/update": (draft: Draft<customCheckoutState>, action: { notes: string }) => {
        draft.notes = action.notes;
    },
    "xCheckoutCarrier/update": (draft: Draft<customCheckoutState>, action: { carrier: string }) => {
        draft.carrier = action.carrier;
    },
    "xCheckoutShipVia/update": (draft: Draft<customCheckoutState>, action: { shipVia: string }) => {
        draft.shipVia = action.shipVia;
    },
    "xCheckoutNameOfCarrier/update": (draft: Draft<customCheckoutState>, action: { nameOfCarrier: string }) => {
        draft.nameOfCarrier = action.nameOfCarrier;
    },
    "xCheckoutAccountNumber/update": (draft: Draft<customCheckoutState>, action: { accountNumber: string }) => {
        draft.accountNumber = action.accountNumber;
    },
    "xCheckoutShippingNotes/update": (draft: Draft<customCheckoutState>, action: { shippingNotes: string }) => {
        draft.shippingNotes = action.shippingNotes;
    },
    "xCheckout/setLastFour": (draft: Draft<customCheckoutState>, action: { lastFour: string }) => {
        draft.lastFour = action.lastFour;
    },
    "xCheckout/setExpDateMon": (draft: Draft<customCheckoutState>, action: { expDateMon: string }) => {
        draft.expDateMon = action.expDateMon;
    },
    "xCheckout/setExpDateYear": (draft: Draft<customCheckoutState>, action: { expDateYear: string }) => {
        draft.expDateYear = action.expDateYear;
    },
    "xCheckout/setCcType": (draft: Draft<customCheckoutState>, action: { ccType: string }) => {
        draft.ccType = action.ccType;
    },
    "xCheckout/setPo": (draft: Draft<customCheckoutState>, action: { po: string }) => {
        draft.po = action.po;
    },
    "xCheckout/setPaymentMethod": (draft: Draft<customCheckoutState>, action: { paymentMethod: string }) => {
        draft.paymentMethod = action.paymentMethod;
    },
    "xCheckout/setPaymentProfile": (draft: Draft<customCheckoutState>, action: { paymentProfile: string }) => {
        draft.paymentProfile = action.paymentProfile;
    },
    "xCheckout/setContractNumber": (draft: Draft<customCheckoutState>, action: { contractNumber: string }) => {
        draft.contractNumber = action.contractNumber;
    },
    "xCheckout/setIsEditingAddress": (draft: Draft<customCheckoutState>, action: { isEditingAddress: boolean }) => {
        draft.isEditingAddress = action.isEditingAddress;
    },
    "xCheckout/setShowCheckoutFormErrors": (draft: Draft<customCheckoutState>, action: { showErrors: boolean }) => {
        draft.showErrors = action.showErrors;
    },
    "xCheckoutSetPaymentReady/update": (draft: Draft<customCheckoutState>, action: { isPaymentReady: boolean }) => {
        draft.isPaymentReady = action.isPaymentReady;
    },
    "xCheckoutSetUseBillingAddress/update": (
        draft: Draft<customCheckoutState>,
        action: { useBillingAddress: boolean },
    ) => {
        draft.useBillingAddress = action.useBillingAddress;
    },
    "xCheckoutAssociatedSalesRep/update": (
        draft: Draft<customCheckoutState>,
        action: { updateAssociatedSalesRep: boolean; associatedSalesRep: string },
    ) => {
        draft.updateAssociatedSalesRep = action.updateAssociatedSalesRep;
        draft.associatedSalesRep = action.associatedSalesRep;
    },

    "xCheckout/clearCustomStore": (draft: Draft<customCheckoutState>) => {
        draft.checkoutStep = 0;
        draft.notes = "";
        draft.carrier = "";
        draft.shipVia = "";
        draft.nameOfCarrier = "";
        draft.accountNumber = "";
        draft.shippingNotes = "";
        draft.lastFour = "";
        draft.expDateMon = "";
        draft.expDateYear = "";
        draft.ccType = "";
        draft.po = "";
        draft.paymentMethod = "";
        draft.paymentProfile = "";
        draft.contractNumber = "";
        draft.isEditingAddress = false;
        draft.isPaymentReady = false;
        draft.useBillingAddress = true;
        draft.associatedSalesRep = "";
        draft.updateAssociatedSalesRep = false;
    },
};

const xCheckout = createTypedReducerWithImmer(initialCustomCheckoutState, reducer);

(reducers as any).xCheckout = xCheckout;

export type xCheckoutState = {
    xCheckout: customCheckoutState;
};

type xCheckoutReducers = {
    checkoutStep: typeof xCheckout;
    notes: typeof xCheckout;
    carrier: typeof xCheckout;
    shipVia: typeof xCheckout;
    nameOfCarrier: typeof xCheckout;
    accountNumber: typeof xCheckout;
    lastFour: typeof xCheckout;
    shippingNotes: typeof xCheckout;
    expDateMon: typeof xCheckout;
    expDateYear: typeof xCheckout;
    ccType: typeof xCheckout;
    po: typeof xCheckout;
    paymentMethod: typeof xCheckout;
    paymentProfile: typeof xCheckout;
    contractNumber: typeof xCheckout;
};

export type xCheckoutActions = Parameters<xCheckoutReducers[keyof xCheckoutReducers]>[1] | AnyAction;
